import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  EmojiEvents,
  TrendingUp,
  Psychology,
  ArrowBack,
  Replay,
  Timer,
  Chat,
} from '@mui/icons-material';
import { api } from '@/services/api';
import { useSessionStore } from '@/stores';
import { SessionResult, CapabilityResult, StakeholderResult, NarrativeOutcome, AchievementResult, BehavioralProfile, ProfileDimension, CommunicationAnalysis } from '@/types';

const EDGEHUB_URL = import.meta.env.VITE_EDGEHUB_URL || 'http://localhost:3000';

export default function ResultsView() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { isEdgeHubSession, edgeHubUserId } = useSessionStore();
  const [result, setResult] = useState<SessionResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    const fetchResults = async () => {
      try {
        const data = await api.getSessionResults(sessionId);
        setResult(data);
      } catch (err) {
        setError('Failed to load results. The session may not be completed yet.');
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [sessionId]);

  const handleTryAgain = async () => {
    if (!result) return;
    try {
      const session = await api.createSession({
        userId: edgeHubUserId || '',
        scenarioId: result.scenarioId,
      });
      navigate(`/simulation/${session.id}`);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        setError('Your session has expired. Please return to EdgeHub to continue.');
      } else if (status === 403) {
        setError('Replay not allowed — your organization\'s policy permits one attempt per scenario.');
      } else {
        setError('Failed to create new session');
      }
    }
  };

  const handleReturnToEdgeHub = () => {
    window.location.href = `${EDGEHUB_URL}/on-demand`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Calculating your results...</Typography>
      </Box>
    );
  }

  if (error || !result) {
    return (
      <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error">{error || 'Results not available'}</Alert>
        <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>Back to Dashboard</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 3, pb: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Session Results
        </Typography>
        <Typography variant="h6" color="text.secondary">
          The Inbox Crisis
        </Typography>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Chip
            icon={<Timer />}
            label={result.timeManagementSummary}
            variant="outlined"
          />
          <Chip
            icon={<TrendingUp />}
            label={`${result.totalXpEarned} Total XP`}
            color="primary"
          />
        </Box>
      </Box>

      {/* EdgeHub Integration Banner */}
      {isEdgeHubSession && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Your results have been saved to your EdgeHub profile.
        </Alert>
      )}

      {/* Qualitative Scores */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Overview
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <QualitativeBadge label="Risk" value={result.riskScore} />
          <QualitativeBadge label="Leadership" value={result.leadershipScore} />
        </Box>
      </Paper>

      {/* Communication Analysis */}
      {result.communicationAnalysis && result.communicationAnalysis.totalMessagesSent > 0 && (
        <CommunicationAnalysisSection analysis={result.communicationAnalysis} />
      )}

      {/* Behavioral Profile */}
      {result.behavioralProfile && (
        <BehavioralProfileSection profile={result.behavioralProfile} />
      )}

      {/* Stakeholder Impact */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Stakeholder Impact
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {Object.entries(result.stakeholderImpact).map(([id, stakeholder]) => (
            <StakeholderRow key={id} stakeholder={stakeholder} />
          ))}
        </Box>
      </Paper>

      {/* Capability Progress */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
          Capability Progress
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Object.entries(result.capabilityScores)
            .sort(([, a], [, b]) => b.xpEarned - a.xpEarned)
            .map(([id, cap]) => (
              <CapabilityRow key={id} capability={cap} />
            ))}
        </Box>
      </Paper>

      {/* Narrative Outcomes */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          What Happened Next
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {Object.entries(result.narrativeOutcomes).map(([id, narrative]) => (
            <NarrativeCard key={id} narrative={narrative} />
          ))}
        </Box>
      </Paper>

      {/* Achievements */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          <EmojiEvents sx={{ mr: 1, verticalAlign: 'middle' }} />
          Achievements
        </Typography>
        {result.achievementsEarned.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {result.achievementsEarned.map((achievement) => (
              <AchievementBadge key={achievement.id} achievement={achievement} />
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary">None earned this session</Typography>
        )}
      </Paper>

      {/* Patterns Detected */}
      {result.patternsDetected.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            <Psychology sx={{ mr: 1, verticalAlign: 'middle' }} />
            Patterns Detected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {result.patternsDetected.map((pattern) => (
              <Chip
                key={pattern.patternId}
                label={`${pattern.label} (+${pattern.bonusXP} ${pattern.bonusCapability} XP)`}
                color={pattern.positive ? 'success' : 'warning'}
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Decision Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Your Decisions
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {result.decisions.map((decision, i) => (
            <Box key={decision.decisionId}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Decision {i + 1}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={500}>{decision.prompt}</Typography>
              <Typography variant="body2" color="primary.main" sx={{ mt: 0.5 }}>
                {decision.chosenOptionText}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                {decision.feedback}
              </Typography>
              {i < result.decisions.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
        <Button
          variant="contained"
          startIcon={<Replay />}
          onClick={handleTryAgain}
          size="large"
        >
          Try Again
        </Button>
        {isEdgeHubSession ? (
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleReturnToEdgeHub}
            size="large"
          >
            Return to EdgeHub
          </Button>
        ) : (
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            size="large"
          >
            Home
          </Button>
        )}
      </Box>
    </Box>
  );
}

// Sub-components

function QualitativeBadge({ label, value }: { label: string; value: string }) {
  const color = () => {
    const v = value.toLowerCase();
    if (v.includes('low') || v.includes('strong') || v.includes('good')) return 'success';
    if (v.includes('medium') || v.includes('developing')) return 'warning';
    return 'error';
  };

  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.5, textAlign: 'center', minWidth: 140 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="subtitle1" fontWeight={600}>
        <Chip label={value} color={color()} size="small" />
      </Typography>
    </Paper>
  );
}

function StakeholderRow({ stakeholder }: { stakeholder: StakeholderResult }) {
  const scoreColor = stakeholder.score > 0 ? 'success.main' : stakeholder.score < 0 ? 'error.main' : 'text.secondary';
  const scorePrefix = stakeholder.score > 0 ? '+' : '';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" fontWeight={500}>{stakeholder.name}</Typography>
        <Typography variant="caption" color="text.secondary">{stakeholder.role}</Typography>
      </Box>
      <Typography variant="body2" fontWeight={600} sx={{ color: scoreColor }}>
        {scorePrefix}{stakeholder.score}
      </Typography>
      <Chip
        label={stakeholder.label}
        size="small"
        color={stakeholder.score > 0 ? 'success' : stakeholder.score < 0 ? 'error' : 'default'}
        variant="outlined"
        sx={{ minWidth: 90 }}
      />
    </Box>
  );
}

function CapabilityRow({ capability }: { capability: CapabilityResult }) {
  const progress = ((capability.newLevel - Math.floor(capability.newLevel)) / 1) * 100;
  const leveledUp = Math.floor(capability.newLevel) > Math.floor(capability.previousLevel);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="body2" fontWeight={500}>
          {capability.name}
          {leveledUp && (
            <Chip label="Level Up!" size="small" color="warning" sx={{ ml: 1 }} />
          )}
        </Typography>
        <Typography variant="body2" color="primary.main" fontWeight={600}>
          +{capability.xpEarned} XP
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
          L{capability.previousLevel.toFixed(1)}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ flex: 1, height: 8, borderRadius: 4 }}
        />
        <Typography variant="caption" fontWeight={600} sx={{ minWidth: 40 }}>
          L{capability.newLevel.toFixed(1)}
        </Typography>
      </Box>
    </Box>
  );
}

function NarrativeCard({ narrative }: { narrative: NarrativeOutcome }) {
  const borderColor = narrative.sentiment === 'positive' ? 'success.main'
    : narrative.sentiment === 'negative' ? 'error.main'
    : 'divider';

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderLeft: 4, borderLeftColor: borderColor }}
    >
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {narrative.title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {narrative.text}
      </Typography>
    </Paper>
  );
}

function AchievementBadge({ achievement }: { achievement: AchievementResult }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        textAlign: 'center',
        minWidth: 120,
        transition: 'transform 0.2s',
        '&:hover': { transform: 'scale(1.05)' },
      }}
    >
      <Typography variant="h4">{achievement.icon}</Typography>
      <Typography variant="caption" fontWeight={600} display="block">
        {achievement.name}
      </Typography>
      <Typography variant="caption" color="primary.main">
        +{achievement.xpBonus} XP
      </Typography>
    </Paper>
  );
}

function CommunicationAnalysisSection({ analysis }: { analysis: CommunicationAnalysis }) {
  const qualityColor = analysis.overallQualityScore >= 75 ? 'success' :
    analysis.overallQualityScore >= 50 ? 'primary' : 'warning';

  const toneColor = analysis.professionalTonePercent >= 80 ? 'success' :
    analysis.professionalTonePercent >= 60 ? 'primary' : 'warning';

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        <Chat sx={{ mr: 1, verticalAlign: 'middle' }} />
        Communication Analysis
      </Typography>

      {/* Summary stats */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, textAlign: 'center', minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Messages Sent</Typography>
          <Typography variant="h6" fontWeight={700}>{analysis.totalMessagesSent}</Typography>
          <Typography variant="caption" color="text.secondary">
            {analysis.emailsSent} email{analysis.emailsSent !== 1 ? 's' : ''}, {analysis.slackMessagesSent} Slack
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, textAlign: 'center', minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Quality</Typography>
          <Typography variant="h6" fontWeight={700}>
            <Chip label={analysis.overallQualityLabel} color={qualityColor} size="small" />
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, textAlign: 'center', minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Professional Tone</Typography>
          <Typography variant="h6" fontWeight={700}>
            <Chip label={`${Math.round(analysis.professionalTonePercent)}%`} color={toneColor} size="small" />
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ px: 2, py: 1.5, textAlign: 'center', minWidth: 100 }}>
          <Typography variant="caption" color="text.secondary">Proactiveness</Typography>
          <Typography variant="h6" fontWeight={700}>
            <Chip label={`${Math.round(analysis.proactivenessScore)}%`} color={analysis.proactivenessScore >= 50 ? 'success' : 'warning'} size="small" />
          </Typography>
        </Paper>
      </Box>

      {/* Quality bar */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" fontWeight={500}>Overall Communication Quality</Typography>
          <Typography variant="body2" fontWeight={600} color={`${qualityColor}.main`}>
            {Math.round(analysis.overallQualityScore)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={analysis.overallQualityScore}
          color={qualityColor}
          sx={{ height: 10, borderRadius: 5 }}
        />
      </Box>

      {/* Message breakdown */}
      {analysis.messageBreakdown.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
            Message Breakdown
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {analysis.messageBreakdown.map((msg, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Chip
                  label={msg.channel}
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 60, textTransform: 'capitalize' }}
                />
                <Typography variant="body2" sx={{ flex: 1 }} fontWeight={500}>
                  To: {msg.recipient}
                </Typography>
                <Chip
                  label={msg.quality}
                  size="small"
                  color={msg.quality === 'excellent' ? 'success' : msg.quality === 'good' ? 'primary' : msg.quality === 'poor' ? 'error' : 'default'}
                  variant="outlined"
                  sx={{ minWidth: 80, textTransform: 'capitalize' }}
                />
                <Chip
                  label={msg.tone}
                  size="small"
                  variant="outlined"
                  sx={{ minWidth: 80, textTransform: 'capitalize' }}
                />
              </Box>
            ))}
          </Box>
        </>
      )}
    </Paper>
  );
}

function BehavioralProfileSection({ profile }: { profile: BehavioralProfile }) {
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        <Psychology sx={{ mr: 1, verticalAlign: 'middle' }} />
        Your Leadership Style
      </Typography>

      {/* Dominant Style */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} color="primary.main">
          {profile.dominantStyle}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {profile.styleDescription}
        </Typography>
      </Box>

      {/* Dimension Bars */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
        Behavioral Dimensions
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Based on your decisions and workplace behavior during the simulation
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        {profile.dimensions.map((dim) => (
          <DimensionBar key={dim.id} dimension={dim} />
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Strengths & Growth Areas */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {profile.strengths.length > 0 && (
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <Typography variant="subtitle2" fontWeight={600} color="success.main" gutterBottom>
              Strengths
            </Typography>
            {profile.strengths.map((s, i) => (
              <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {s}
              </Typography>
            ))}
          </Box>
        )}
        {profile.growthAreas.length > 0 && (
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <Typography variant="subtitle2" fontWeight={600} color="warning.main" gutterBottom>
              Growth Areas
            </Typography>
            {profile.growthAreas.map((g, i) => (
              <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {g}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    </Paper>
  );
}

function DimensionBar({ dimension }: { dimension: ProfileDimension }) {
  const barColor = dimension.score >= 66 ? 'success' : dimension.score >= 36 ? 'primary' : 'warning';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="body2" fontWeight={500}>
          {dimension.name}
        </Typography>
        <Chip
          label={dimension.level}
          size="small"
          color={barColor}
          variant="outlined"
          sx={{ minWidth: 80 }}
        />
      </Box>
      <LinearProgress
        variant="determinate"
        value={dimension.score}
        color={barColor}
        sx={{ height: 10, borderRadius: 5 }}
      />
    </Box>
  );
}
